import { Minifiable } from '@optimus-web/common';
import { Step, StepDescription } from './Step';
import { NavElement, ProgressIndicatorContainer } from './style';

export interface ProgressIndicatorProps<S extends string = string> extends Minifiable {
    steps: StepDescription<S>[];
    activeStep?: S;
    onStepChange?: (value: S) => void;
    vertical?: boolean;
    isOpened?: boolean;
    isOpenable?: boolean;
}

export const ProgressIndicator = <S extends string = string>({
    steps,
    activeStep,
    onStepChange,
    vertical,
    small,
    isOpened,
    isOpenable = false,
    ...other
}: ProgressIndicatorProps<S>) => {
    const activeItemIndex = steps.findIndex((item) => item.value === activeStep);
    const singleItem = steps.length === 1;

    return (
        <NavElement aria-label="progress" isVerticalOpen={vertical && isOpened}>
            <ProgressIndicatorContainer vertical={vertical} {...other}>
                {steps.map(({ value, completed, order, label, optional, editable, ariaLabel }, index) => {
                    const isActive = activeStep === value;
                    const isCompleted = completed || activeItemIndex > index;
                    const nextIsCompleted = steps[index + 1]?.completed;
                    const nextIsActive = activeStep === steps[index + 1]?.value;
                    const isPrimary = nextIsActive || nextIsCompleted;
                    const nextIsEditable = steps[index + 1]?.editable;
                    const nextIsVisited = nextIsActive || nextIsCompleted || nextIsEditable;

                    const isLastStep = index === steps.length - 1;
                    const isVerticalOpenActiveStep = vertical && isOpened && isActive;

                    return (
                        <Step
                            key={value}
                            index={order || index}
                            value={value}
                            onClick={onStepChange}
                            completed={isCompleted}
                            active={isActive}
                            vertical={vertical}
                            small={small}
                            singleItem={singleItem}
                            label={label}
                            optional={optional}
                            editable={editable}
                            ariaLabel={ariaLabel}
                            isLastStep={isLastStep}
                            isPrimary={isPrimary}
                            nextIsVisited={nextIsVisited}
                            isVerticalOpenActiveStep={isVerticalOpenActiveStep}
                            stepsCount={steps.length}
                            isOpenable={isOpenable}
                            isOpened={isOpened}
                        />
                    );
                })}
            </ProgressIndicatorContainer>
        </NavElement>
    );
};
